import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useLocation, useNavigate } from 'react-router-dom';
import { AssignedTask } from '../utils/TaskMaster';
import { getSchedules, getAllAssignedTasks, updateAssignedTask } from '../utils/api';
/**
 * Adjust these import paths to match your app:
 * - AssignedTasks is the page-level runner you uploaded (a.k.a. TaskRunner).
 * - Timer is your utility used for percent/time math.
 */
import TaskRunner from "../components/AssignedTasks"; // e.g. src/pages/AssignedTasks
import Timer from "../utils/timer";              // e.g. src/utils/timer

export const categoryFiltersForTimeTrialMode = (mode) => {
  if (mode === 'uncategorized') return { uncategorized: true };
  if (!mode?.startsWith('category:')) return {};
  const categoryId = Number(mode.slice('category:'.length));
  if (!Number.isInteger(categoryId)) throw new Error('Unknown Time Trial category');
  return { category_id: [categoryId] };
};

export const activeScheduleCategoryFilters = (schedules, now = new Date()) => {
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'short' });
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const categoryIds = new Set();
  let uncategorized = false;

  schedules.forEach((schedule) => {
    const [startHour, startMinute] = schedule.start.split(':').map(Number);
    const [endHour, endMinute] = schedule.end.split(':').map(Number);
    const active = schedule.days.includes(currentDay)
      && currentMinutes >= startHour * 60 + startMinute
      && currentMinutes <= endHour * 60 + endMinute;
    if (!active) return;
    if (schedule.category_id == null) uncategorized = true;
    else categoryIds.add(schedule.category_id);
  });

  if (!categoryIds.size && !uncategorized) return { uncategorized: true };
  return {
    category_id: Array.from(categoryIds),
    ...(uncategorized ? { uncategorized: true } : {}),
  };
};

/**
 * Props
 * - assignedTasks: array of tasks for AssignedTasks
 * - sessionDurationSec: number    -> total length of the **page** timer (top clock & border)
 * - sessionStartAt?: Date|string  -> when the session started (ISO/string/Date). If omitted, it starts now.
 * - totalBoxes: number            -> how many boxes in the "Tasks to complete" bar
 *
 * Notes
 * - The bottom time summary from AssignedTasks is disabled (showTimeSummary={false}).
 * - We detect task completion via handleUpdateTask: when a task's `complete` is set,
 *   we bump the filled boxes (up to totalBoxes).
 */
function ArcadeAssignedTasksPage({
  assignedTasks,
  sessionDurationSec,
  sessionStartAt,
  totalBoxes,
  handleUpdateTask: propHandleUpdateTask,
  onBackToHome
}) {
  // ---- Progress boxes (fills one per completed task) ----
  const [filledBoxes, setFilledBoxes] = useState(0);

  // Keep a set of task ids we've already counted so we don't double-count.
  const countedIdsRef = useRef(new Set());

  // Ref to access the back home button from TaskRunner
  const backHomeButtonRef = useRef(null);

  const handleUpdateTask = (task) => {
    // Call the prop handler (API update)
    const updateResult = propHandleUpdateTask ? propHandleUpdateTask(task) : null;
    // If the task just became complete, count it (once).
    if (task?.complete && task?.id && !countedIdsRef.current.has(task.id)) {
      countedIdsRef.current.add(task.id);
      setFilledBoxes((n) => Math.min(totalBoxes ?? 0, n + 1));
    }
    return updateResult;
  };

  // ---- Session timer for the page frame & top clock ----
  const [displayTime, setDisplayTime] = useState("00:00:00");
  const [percentLeft, setPercentLeft] = useState(100);

  // Build a Timer that represents the *overall page session*, with no prior workTime.
  const timerRef = useRef(null);
  useEffect(() => {
    // workTime = 0 here, we just want a straight countdown
    const t = new Timer(sessionDurationSec ?? 0, 0);
    timerRef.current = t;

    const startAt =
      sessionStartAt ? new Date(sessionStartAt) : new Date();

    // Start your Timer using its public API
    t.onUpdate = (label, p) => {
      setDisplayTime(label);
      setPercentLeft(p);
    };
    t.onComplete = () => {
      setDisplayTime(t.getTimeLeft()); // will show 00:00:00 or negative prefix per your util
      setPercentLeft(0);
      // Click the back home button to update current task and navigate home when session timer runs out
      backHomeButtonRef.current?.click();
    };
    t.start(startAt);

    // Prime UI immediately
    setDisplayTime(t.getTimeLeft());
    setPercentLeft(t.getPercentLeft());

    return () => {
      t.stop?.();
    };
  }, [sessionDurationSec, sessionStartAt]);

  // ---- Colors: green ➜ orange at 50% ➜ pink at 0% (smoothly) ---- (using theme colors)
  const frameColor = useMemo(() => {
    // map 100..50 linearly green->orange, then 50..0 orange->pink
    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
    const p = clamp(percentLeft, 0, 100);

    // Helper to lerp between two hex colors
    const lerpColor = (a, b, t) => {
      const ah = a.replace("#", "");
      const bh = b.replace("#", "");
      const ar = parseInt(ah.substring(0, 2), 16);
      const ag = parseInt(ah.substring(2, 4), 16);
      const ab = parseInt(ah.substring(4, 6), 16);
      const br = parseInt(bh.substring(0, 2), 16);
      const bg = parseInt(bh.substring(2, 4), 16);
      const bb = parseInt(bh.substring(4, 6), 16);
      const rr = Math.round(ar + (br - ar) * t).toString(16).padStart(2, "0");
      const rg = Math.round(ag + (bg - ag) * t).toString(16).padStart(2, "0");
      const rb = Math.round(ab + (bb - ab) * t).toString(16).padStart(2, "0");
      return `#${rr}${rg}${rb}`;
    };

    const GREEN = "#39FF14";  // theme green
    const ORANGE = "#FF5F1F"; // theme orange
    const PINK = "#FF2D95";   // theme secondary

    if (p >= 50) {
      // 100..50 -> 0..1
      const t = (100 - p) / 50;
      return lerpColor(GREEN, ORANGE, t);
    } else {
      // 50..0 -> 0..1
      const t = (50 - p) / 50;
      return lerpColor(ORANGE, PINK, t);
    }
  }, [percentLeft]);

  // ---- Frame style: conic-gradient mask to show only a thick border ----
  const frameStyle = useMemo(() => {
    const remaining = Math.max(0, Math.min(100, percentLeft));
    // The colored arc shows what’s left (clockwise from top).
    // The rest of the circle is transparent.
    const arc = `conic-gradient(${frameColor} 0% ${remaining}%, transparent ${remaining}% 100%)`;

    // Create a ring by masking out the center; works in modern browsers.
    return {
      position: "relative",
      padding: "14px",
      backgroundImage: arc,
      WebkitMask:
        "radial-gradient(calc(100% - 10px) circle, #0000 0, #0000 calc(100% - 12px), #000  calc(100% - 11px))", // outer ring (fallback)
      mask:
        "radial-gradient(calc(100% - 10px) circle, rgba(0,0,0,0) 0, rgba(0,0,0,0) calc(100% - 12px), #000 calc(100% - 11px))",
      borderRadius: "18px",
    };
  }, [percentLeft, frameColor]);

  // Simple pixel-style progress bar (N boxes) - using theme colors
const ProgressBar = ({ total, filled }) => (
  <Box
    id="time-trial-progress"
    display="inline-block"
    justifyContent="center"
    alignItems="center"
    sx={{border:"2px solid", borderColor: "secondary.main", borderRadius: 2,display:"inline-block"}}
    >
    <Grid container justifyContent="center" alignItems="center" sx={{ width: "auto" }}>
      {Array.from({ length: total || 0 }).map((_, i) => (
        <Grid item key={i}>
          <Box
            sx={{
              width: 36,
              height: 20,
              border: (theme) => `3px solid ${theme.palette.secondary.main}`,
              backgroundColor: (theme) => i < filled ? theme.palette.green.main : "transparent",
              boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.background.default}`,
            }}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
);

  return (
    <Box id="time-trial-session" sx={frameStyle}>
      <Box
        id="time-trial-session-panel"
        sx={{
          borderRadius: 3,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 60%, ${theme.palette.background.default} 100%)`,
          p: 2,
        }}
      >
        {/* Top session timer */}
        <Typography
          id="time-trial-countdown"
          variant="h1"
          sx={{
            textAlign: "center",
            color: "primary.main",
            fontWeight: 800,
            lineHeight: 1,
            mb: 1,
          }}
        >
          {displayTime}
        </Typography>

        {/* "Tasks to complete" + progress boxes */}
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            color: "secondary.main",
            fontWeight: 700,
            letterSpacing: 2,
            mb: 1,
          }}
        >
          Tasks To Complete
        </Typography>
        <ProgressBar total={totalBoxes} filled={filledBoxes} />

        {/* AssignedTasks (bottom time summary hidden) */}
        <Box id="time-trial-task-runner" sx={{ mt: 1.5 }}>
          <TaskRunner
            assignedTasks={assignedTasks}
            handleUpdateTask={handleUpdateTask}
            onBackToHome={onBackToHome}
            backHomeButtonRef={backHomeButtonRef}
            showTimeSummary={false}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function TimeTrial() {
  const location = useLocation();
  const navigate = useNavigate();
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [sessionDurationSec, setSessionDurationSec] = useState(0);
  const [loading, setLoading] = useState(true);

  const goHome = () => {
    navigate('/user-home');
  };

  useEffect(() => {
    const settings = location.state?.settings;
    if (!settings) {
      navigate('/time-trial-settings');
      return;
    }

    const fetchTasks = async () => {
      try {
        let tasks = [];
        const mode = settings.mode;
        const sessionSec = settings.time * 60;
        let filters = {"order_by":"order","completed":false};
        if (mode === 'schedule') {
          const schedules = await getSchedules();
          filters = { ...filters, ...activeScheduleCategoryFilters(schedules) };
          const data = await getAllAssignedTasks(1, 100, filters);
          tasks = data.tasks.filter(task => task.complete === null).map(item => new AssignedTask(item));
        } else if (mode === 'priority') {
          const data = await getAllAssignedTasks(1, 100, filters);
          tasks = data.tasks.map(item => new AssignedTask(item));
        } else if (mode === 'uncategorized' || mode.startsWith('category:')) {
          filters = { ...filters, ...categoryFiltersForTimeTrialMode(mode) };
          const data = await getAllAssignedTasks(1, 100, filters);
          tasks = data.tasks.filter(task => task.complete === null).map(item => new AssignedTask(item));
        } else {
          throw new Error('Unknown Time Trial mode');
        }

        // Sort by order, then by time remaining
        tasks.sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          const aRemaining = Math.max(0, (a.duration || 0) - (a.working_time || 0));
          const bRemaining = Math.max(0, (b.duration || 0) - (b.working_time || 0));
          return aRemaining - bRemaining;
        });

        // Calculate totalBoxes: max tasks that fit in session time
        let cumulative = 0;
        let boxes = 0;
        for (const task of tasks) {
          const remaining = Math.max(0, (task.duration || 0) - (task.working_time || 0));
          if (cumulative + remaining <= sessionSec) {
            cumulative += remaining;
            boxes++;
          } else {
            break;
          }
        }

        setAssignedTasks(tasks);
        setTotalBoxes(boxes);
        setSessionDurationSec(sessionSec);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [location.state, navigate]);

  const handleUpdateTask = async (task) => {
    try {
      console.log('Updating task:', task.toJSON());
      const data = await updateAssignedTask(task.id, task.toJSON());
      console.log('Task updated successfully', data);
      return data;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };



  if (loading) {
    return (
      <Box
        id="time-trial-loading"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography variant="h4" color="primary.main">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <ArcadeAssignedTasksPage
      assignedTasks={assignedTasks}
      sessionDurationSec={sessionDurationSec}
      totalBoxes={totalBoxes}
      handleUpdateTask={handleUpdateTask}
      onBackToHome={goHome}
    />
  );
}
